import React from "react";
import { AlertTriangle, Plus } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

const SEV_COLORS = {
  critical: "bg-red-100 text-red-700",
  high: "bg-orange-100 text-orange-700",
  medium: "bg-amber-100 text-amber-700",
  low: "bg-slate-100 text-slate-600",
};

const RISK_CATEGORIES = [
  { key: "missing_clause", label: "Missing Clauses" },
  { key: "compliance", label: "Compliance" },
  { key: "scope_risk", label: "Scope Risks" },
  { key: "financial_risk", label: "Financial Risks" },
  { key: "operational_risk", label: "Operational Risks" },
  { key: "jurisdictional_risk", label: "Jurisdictional Risks" },
];

function RiskScoreHeader({ score, summary }) {
  const label = score >= 7 ? "High Risk" : score >= 4 ? "Moderate Risk" : "Low Risk";
  const bg = score >= 7 ? "bg-red-100 text-red-700" : score >= 4 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
  return (
    <div className="flex items-center gap-3 p-3 rounded-sm bg-gradient-to-r from-amber-50 to-white border border-amber-100" data-testid="risk-score">
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold ${bg}`}>{score}/10</div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-700">{label}</p>
        <p className="text-xs text-slate-500 mt-0.5">{summary}</p>
      </div>
    </div>
  );
}

function RiskCategorySection({ catKey, label, risks, onApplySuggestion }) {
  if (risks.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] font-mono uppercase text-slate-500 mb-1 flex items-center gap-1">
        {label} <Badge variant="outline" className="text-[9px]">{risks.length}</Badge>
      </p>
      {risks.map(risk => (
        <div key={risk.id} className="border rounded-sm p-2.5 mb-1.5 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <Badge className={`text-[9px] rounded-sm ${SEV_COLORS[risk.severity] || SEV_COLORS.medium}`}>{risk.severity}</Badge>
            <span className="text-xs font-medium">{risk.title}</span>
          </div>
          <p className="text-[11px] text-slate-600">{risk.description}</p>
          <div className="bg-cyan-50 border border-cyan-200 rounded-sm p-1.5">
            <p className="text-[9px] font-mono uppercase text-cyan-600 mb-0.5">Recommendation</p>
            <p className="text-[11px] text-slate-700">{risk.recommendation}</p>
          </div>
          {risk.clause_suggestion && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-sm p-1.5">
              <p className="text-[9px] font-mono uppercase text-emerald-500 mb-0.5">Suggested Clause</p>
              <p className="text-[11px] text-emerald-800 italic">{risk.clause_suggestion}</p>
              {onApplySuggestion && (
                <Button size="sm" variant="outline" className="mt-1 h-5 text-[9px] gap-1 rounded-sm border-emerald-300 text-emerald-700"
                  onClick={() => onApplySuggestion(risk.title, risk.clause_suggestion, true)}>
                  <Plus size={9} /> Add
                </Button>
              )}
            </div>
          )}
          {risk.jurisdiction_note && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-1.5">
              <p className="text-[9px] font-mono uppercase text-indigo-600 mb-0.5">Jurisdiction Note</p>
              <p className="text-[11px] text-slate-700">{risk.jurisdiction_note}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function JurisdictionAnalysis({ analysis }) {
  if (!analysis) return null;
  const riskColor = analysis.overall_jurisdiction_risk === "high" ? "bg-red-100 text-red-700" :
    analysis.overall_jurisdiction_risk === "medium" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700";
  return (
    <div className="p-2.5 border rounded-sm bg-indigo-50/50 border-indigo-200">
      <p className="text-[10px] font-mono uppercase text-indigo-600 mb-2">
        Jurisdictional Intelligence — {analysis.jurisdiction}
      </p>
      <div className="grid grid-cols-2 gap-2 text-xs mb-2">
        <div>
          <p className="text-[9px] text-slate-500 uppercase">Governing Law Compatible</p>
          <p className={`font-medium text-xs ${analysis.governing_law_compatible ? "text-emerald-600" : "text-red-600"}`}>
            {analysis.governing_law_compatible ? "Yes" : "No"}
          </p>
        </div>
        <div>
          <p className="text-[9px] text-slate-500 uppercase">Jurisdiction Risk</p>
          <Badge className={`text-[9px] ${riskColor}`}>{analysis.overall_jurisdiction_risk}</Badge>
        </div>
      </div>
      {analysis.mandatory_missing_clauses?.length > 0 && (
        <div className="mb-1.5">
          <p className="text-[9px] text-slate-500 uppercase mb-0.5">Mandatory Missing Clauses</p>
          {analysis.mandatory_missing_clauses.map((c, i) => (
            <p key={`miss-${i}`} className="text-[11px] text-red-700 flex items-start gap-1"><span className="shrink-0">-</span>{c}</p>
          ))}
        </div>
      )}
      {analysis.key_considerations?.length > 0 && (
        <div>
          <p className="text-[9px] text-slate-500 uppercase mb-0.5">Key Considerations</p>
          {analysis.key_considerations.map((c, i) => (
            <p key={`key-${i}`} className="text-[11px] text-slate-700 flex items-start gap-1"><span className="shrink-0">-</span>{c}</p>
          ))}
        </div>
      )}
    </div>
  );
}

export function ReviewRiskTab({ riskResult, onApplySuggestion }) {
  if (!riskResult) return null;
  const allRisks = riskResult.risks || [];

  return (
    <div className="space-y-3">
      {/* Disclaimer */}
      <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-sm text-[10px] text-amber-800">
        <AlertTriangle size={12} className="shrink-0 mt-0.5" />
        <p>AI-generated risk analysis for informational purposes only. Not legal advice.</p>
      </div>

      <RiskScoreHeader score={riskResult.overall_risk_score} summary={riskResult.summary} />

      {/* Strengths */}
      {riskResult.strengths?.length > 0 && (
        <div>
          <p className="text-[10px] font-mono uppercase text-emerald-600 mb-1">Strengths</p>
          {riskResult.strengths.map((s, i) => (
            <div key={`rs-${i}`} className="flex items-start gap-1.5 text-xs px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-sm mb-1">
              <span className="text-emerald-600 shrink-0">+</span><span className="text-slate-700">{s}</span>
            </div>
          ))}
        </div>
      )}

      {/* Risk items by category */}
      {RISK_CATEGORIES.map(cat => (
        <RiskCategorySection key={cat.key} catKey={cat.key} label={cat.label}
          risks={allRisks.filter(r => r.category === cat.key)} onApplySuggestion={onApplySuggestion} />
      ))}

      <JurisdictionAnalysis analysis={riskResult.jurisdiction_analysis} />

      {/* Stats */}
      <div className="flex gap-3 pt-2 border-t text-[10px] text-slate-400 flex-wrap">
        <span>Total: {allRisks.length}</span>
        <span>Critical: {allRisks.filter(r => r.severity === "critical").length}</span>
        <span>High: {allRisks.filter(r => r.severity === "high").length}</span>
        <span>Medium: {allRisks.filter(r => r.severity === "medium").length}</span>
      </div>
    </div>
  );
}
